import React, { useState } from 'react';
import { getRestaurants } from '@utilities/auth';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Paper,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Hotel as HotelIcon } from '@mui/icons-material';
import { connect, useDispatch } from 'react-redux';
import { getFirestore, doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';

interface RoomDetail {
  name: string;
  description: string;
  pricePerNight: number;
  maxGuests: number;
  totalRooms: number;
  amenities: string[];
  facilities: string[];
  imageUrl: string;
}

interface RoomManagementProps {
  roomDetails: RoomDetail[];
  selectedRestaurant: any;
  roles: any;
}

const RoomManagement: React.FC<RoomManagementProps> = ({ roomDetails, selectedRestaurant, roles }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<RoomDetail | null>(null);
  const [formData, setFormData] = useState<Partial<RoomDetail>>({});
  const dispatch = useDispatch();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleOpen = (room?: RoomDetail) => {
    if (room) {
      setEditingRoom(room);
      setFormData(room);
    } else {
      setEditingRoom(null);
      setFormData({});
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingRoom(null);
    setFormData({});
  };

  const handleSubmit = async () => {
    if (!selectedRestaurant?.docId) {
      setSnackbar({
        open: true,
        message: 'No restaurant selected',
        severity: 'error',
      });
      return;
    }

    try {
      const db = getFirestore();
      const roomRef = doc(db, 'restaurants', selectedRestaurant.docId);
      console.log('roomRef', roomRef, selectedRestaurant.docId);
      const roomData = {
        name: formData.name,
        description: formData.description,
        pricePerNight: Number(formData.pricePerNight),
        maxGuests: Number(formData.maxGuests),
        totalRooms: Number(formData.totalRooms),
        amenities: formData.amenities || [],
        facilities: formData.facilities || [],
        imageUrl: formData.imageUrl,
        updatedAt: new Date(),
      };
      // Check if the document exists
      const docSnapshot = await getDoc(roomRef);
      console.log('roomData', roomData, docSnapshot);
      // await setDoc(roomRef, roomData, { merge: true });
      // Add a new room detail to the array
      await updateDoc(roomRef, {
        roomDetails: arrayUnion(roomData)
      });

      setSnackbar({
        open: true,
        message: editingRoom ? 'Room category updated successfully' : 'Room category added successfully',
        severity: 'success',
      });
      handleClose();
      getRestaurants(dispatch, roles)
    } catch (error) {
      console.error('Error saving room category:', error);
      setSnackbar({
        open: true,
        message: 'Error saving room category',
        severity: 'error',
      });
    }
  };

  const handleDelete = async (roomData: any) => {
    if (!selectedRestaurant?.docId) {
      setSnackbar({
        open: true,
        message: 'No restaurant selected',
        severity: 'error',
      });
      return;
    }

    try {
      const db = getFirestore();
      const roomRef = doc(db, 'restaurants', selectedRestaurant.docId);
      // await deleteDoc(roomRef);
      // Remove a specific room detail (must match exactly)
      await updateDoc(roomRef, {
        roomDetails: arrayRemove(roomData)
      });

      setSnackbar({
        open: true,
        message: 'Room category deleted successfully',
        severity: 'success',
      });
      getRestaurants(dispatch, roles)
    } catch (error) {
      console.error('Error deleting room category:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting room category',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        color: theme.palette.primary.main,
        fontWeight: 600,
        mb: 3
      }}>
        <HotelIcon /> Room Management
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ 
            background: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 500 }}>
                  Room Categories
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleOpen()}
                  startIcon={<HotelIcon />}
                >
                  Add Room Category
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip 
                  label={`Total Categories: ${roomDetails.length}`}
                  color="primary"
                  variant="outlined"
                  sx={{ 
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    '& .MuiChip-label': {
                      fontWeight: 500,
                    },
                  }}
                />
                <Chip 
                  label={`Total Rooms: ${roomDetails.reduce((acc, room) => acc + room.totalRooms, 0)}`}
                  color="info"
                  variant="outlined"
                  sx={{ 
                    borderColor: alpha(theme.palette.info.main, 0.5),
                    '& .MuiChip-label': {
                      fontWeight: 500,
                    },
                  }}
                />
              </Box>

              <Paper 
                elevation={0}
                sx={{ 
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                        <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                          Category
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                          Price/Night
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                          Max Guests
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                          Total Rooms
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                          Amenities
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                          Facilities
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {roomDetails.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No room categories found
                          </TableCell>
                        </TableRow>
                      ) : (
                        roomDetails.map((room) => (
                          <TableRow 
                            key={room.name}
                            sx={{
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                              },
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <img 
                                  src={room.imageUrl} 
                                  alt={room.name}
                                  style={{ 
                                    width: 50, 
                                    height: 50, 
                                    objectFit: 'cover', 
                                    borderRadius: 4,
                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                                  }}
                                />
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                    {room.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {room.description}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                ₹{room.pricePerNight.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {room.maxGuests}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {room.totalRooms}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {room.amenities.slice(0, 3).map((amenity) => (
                                  <Chip 
                                    key={amenity} 
                                    label={amenity} 
                                    size="small"
                                    sx={{ 
                                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                      color: theme.palette.primary.main,
                                      '& .MuiChip-label': {
                                        px: 1,
                                      },
                                    }}
                                  />
                                ))}
                                {room.amenities.length > 3 && (
                                  <Chip 
                                    label={`+${room.amenities.length - 3} more`} 
                                    size="small"
                                    sx={{ 
                                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                      color: theme.palette.primary.main,
                                      '& .MuiChip-label': {
                                        px: 1,
                                      },
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {room.facilities.slice(0, 3).map((facility) => (
                                  <Chip 
                                    key={facility} 
                                    label={facility} 
                                    size="small"
                                    sx={{ 
                                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                                      color: theme.palette.info.main,
                                      '& .MuiChip-label': {
                                        px: 1,
                                      },
                                    }}
                                  />
                                ))}
                                {room.facilities.length > 3 && (
                                  <Chip 
                                    label={`+${room.facilities.length - 3} more`} 
                                    size="small"
                                    sx={{ 
                                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                                      color: theme.palette.info.main,
                                      '& .MuiChip-label': {
                                        px: 1,
                                      },
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Edit">
                                <IconButton 
                                  onClick={() => handleOpen(room)} 
                                  color="primary"
                                  size="small"
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton 
                                  onClick={() => handleDelete(room)} 
                                  color="error"
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add/Edit Room Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRoom ? 'Edit Room Category' : 'Add Room Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Room Name"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
              required
            />
            <TextField
              label="Price per Night"
              type="number"
              value={formData.pricePerNight || ''}
              onChange={(e) => setFormData({ ...formData, pricePerNight: Number(e.target.value) })}
              fullWidth
              required
            />
            <TextField
              label="Max Guests"
              type="number"
              value={formData.maxGuests || ''}
              onChange={(e) => setFormData({ ...formData, maxGuests: Number(e.target.value) })}
              fullWidth
              required
            />
            <TextField
              label="Total Rooms"
              type="number"
              value={formData.totalRooms || ''}
              onChange={(e) => setFormData({ ...formData, totalRooms: Number(e.target.value) })}
              fullWidth
              required
            />
            <TextField
              label="Image URL"
              value={formData.imageUrl || ''}
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Amenities (comma-separated)"
              value={formData.amenities?.join(', ') || ''}
              onChange={(e) => setFormData({ 
                ...formData, 
                amenities: e.target.value.split(',').map(item => item.trim()).filter(Boolean)
              })}
              fullWidth
              required
            />
            <TextField
              label="Facilities (comma-separated)"
              value={formData.facilities?.join(', ') || ''}
              onChange={(e) => setFormData({ 
                ...formData, 
                facilities: e.target.value.split(',').map(item => item.trim()).filter(Boolean)
              })}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!formData.name || !formData.description || !formData.pricePerNight || 
                     !formData.maxGuests || !formData.totalRooms || !formData.imageUrl || 
                     !formData.amenities?.length || !formData.facilities?.length}
          >
            {editingRoom ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const mapStateToProps = (state: any) => {
  return {
    roomDetails: state?.common?.selectedRestaurant?.roomDetails || [],
    selectedRestaurant: state?.common?.selectedRestaurant,
    roles: state?.user?.user?.roles,
  };
};

export default connect(mapStateToProps)(RoomManagement);
